'use client';

import React, { type ReactNode, useEffect } from 'react';
import Bugsnag, { type Event } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { recoverFromChunkLoadError } from '@/app/utils/chunk-recovery';
import { AUTH_REDACTED_KEYS } from '@/app/utils/auth-redaction';

/**
 * OAuth material reaches browser routes in query strings (for example,
 * `interaction_token`, `code`, and magic-link tokens). Bugsnag `redactedKeys`
 * redaction is key-based, so it does not remove query parameters from URL
 * values in requests, referrers, navigation, or network breadcrumbs. Strip
 * every query and fragment before an event is retained or sent.
 */
function scrubUrl(value: unknown): unknown {
  return typeof value === 'string' ? value.replace(/[?#].*$/, '') : value;
}

function scrubBreadcrumbMetadata(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(scrubBreadcrumbMetadata);
  }
  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => {
      const normalizedKey = key.toLowerCase();
      if (['url', 'from', 'to', 'referer', 'referrer'].includes(normalizedKey)) {
        return [key, scrubUrl(child)];
      }
      return [key, scrubBreadcrumbMetadata(child)];
    }),
  );
}

function scrubEventUrls(event: Event): void {
  if (event.request.url) {
    event.request.url = scrubUrl(event.request.url) as string;
  }
  if (event.request.referer) {
    event.request.referer = scrubUrl(event.request.referer) as string;
  }
  event.breadcrumbs.forEach((breadcrumb) => {
    breadcrumb.metadata = scrubBreadcrumbMetadata(breadcrumb.metadata) as Record<string, unknown>;
  });
}

// Initialize Bugsnag only in browser, only once
const isBugsnagEnabled = () => {
  return (
    typeof window !== 'undefined' &&
    process.env.NODE_ENV === 'production' &&
    !!process.env.NEXT_PUBLIC_BUGSNAG_API_KEY
  );
};

// Tag each error with whether the page was being translated in-browser and whether the
// error itself is a hydration error. Google/Microsoft translators mutate text nodes before
// hydration, which throws React #418 mismatches; quantifying the overlap on the Bugsnag
// dashboard confirms translation as the cause. Best-effort: never block the original report.
function addHydrationDiagnostics(event: Event): void {
  try {
    const html = document.documentElement;
    const htmlClass = html.className || '';
    // Google Translate adds `translated-ltr`/`translated-rtl` to <html> and wraps text in <font>.
    const googleTranslated = /(?:^|\s)translated-(?:ltr|rtl)(?:\s|$)/.test(htmlClass);
    // Microsoft/Edge translator stamps `_msttexthash` on translated nodes.
    const microsoftTranslated =
      html.hasAttribute('_msttexthash') ||
      document.querySelector('[_msttexthash]') !== null;
    const fontTagCount = document.getElementsByTagName('font').length;
    const htmlLang = html.lang || '';
    const likelyTranslated =
      googleTranslated ||
      microsoftTranslated ||
      fontTagCount > 0 ||
      htmlLang.toLowerCase() !== 'en';

    const primaryError = event.errors[0];
    const errorText = `${primaryError?.errorClass ?? ''} ${primaryError?.errorMessage ?? ''}`;
    const isHydrationError =
      /Minified React error #(?:418|419|421|422|423|425)/.test(errorText) ||
      /hydrat/i.test(errorText);

    event.addMetadata('diagnostics', {
      likelyTranslated,
      isHydrationError,
      googleTranslated,
      microsoftTranslated,
      fontTagCount,
      htmlLang,
      htmlClass,
      navigatorLanguage: navigator.language,
      navigatorLanguages: navigator.languages?.join(',') ?? '',
    });
  } catch {
    // Diagnostics are best-effort; never let them block the original error report.
  }
}

function startBugsnag(): void {
  if (!isBugsnagEnabled() || Bugsnag.isStarted()) return;

  Bugsnag.start({
    apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY!,
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
    plugins: [new BugsnagPluginReact()],
    releaseStage: process.env.NODE_ENV,
    enabledReleaseStages: ['production'],
    autoTrackSessions: true,
    enabledBreadcrumbTypes: ['error', 'navigation', 'request', 'user'],
    redactedKeys: [...AUTH_REDACTED_KEYS],
    onBreadcrumb: (breadcrumb) => {
      breadcrumb.metadata = scrubBreadcrumbMetadata(breadcrumb.metadata) as Record<string, unknown>;
    },
    onError: (event) => {
      scrubEventUrls(event);
      event.addMetadata('page', {
        url: scrubUrl(window.location.href),
        referrer: scrubUrl(document.referrer),
      });
      addHydrationDiagnostics(event);
      return true;
    },
  });
}

interface ErrorFallbackProps {
  error: Error;
  clearError: () => void;
}

function ErrorFallback({ error, clearError }: ErrorFallbackProps) {
  // Recover from a failed chunk load by reloading the current build (see global-error.tsx
  // for the primary path; this covers chunk errors caught by the inner React boundary).
  useEffect(() => {
    recoverFromChunkLoadError(error);
  }, [error]);

  // Error is automatically reported to Bugsnag by the boundary
  // Log locally for debugging
  console.error('ErrorBoundary caught:', error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center p-8 max-w-md">
        <h1 className="text-2xl font-bold text-zinc-800 mb-4">
          Something went wrong
        </h1>
        <p className="text-zinc-600 mb-6">
          We apologize for the inconvenience. Please try again.
        </p>
        <div className="space-x-4">
          <button
            onClick={clearError}
            className="bg-primary text-white px-6 py-2 rounded hover:opacity-90"
          >
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="border border-zinc-300 text-zinc-700 px-6 py-2 rounded hover:bg-zinc-50"
          >
            Refresh Page
          </button>
        </div>
      </div>
    </div>
  );
}

interface BugsnagProviderProps {
  children: ReactNode;
}

export default function BugsnagProvider({ children }: BugsnagProviderProps) {
  // Initialization happens only when this provider is rendered. The reset
  // credential shell omits it, so merely loading a shared module cannot start
  // third-party delivery while a reset token is in memory.
  if (!isBugsnagEnabled()) {
    return <>{children}</>;
  }
  startBugsnag();

  const ErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary(React);

  if (!ErrorBoundary) {
    return <>{children}</>;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      {children}
    </ErrorBoundary>
  );
}
